import { useEffect, useRef } from 'react';
import { useSocketEvent } from '@/services/socket';
import { isUrgentAlert } from '@/constants/alertTypes';

/** Same key the map's mute toggle already writes, so one switch governs both. */
export const ALERT_SOUND_KEY = 'livemap-sound';

export const isAlertSoundEnabled = (): boolean => {
  try {
    return localStorage.getItem(ALERT_SOUND_KEY) !== 'off';
  } catch {
    return true;
  }
};

/**
 * Rings once when an alert worth interrupting someone for arrives.
 *
 * This used to live inside the live map, which meant a device being unplugged
 * made no sound at all unless the operator happened to have the map open. It
 * belongs at the shell level: the point of a sound is to reach someone who is
 * looking at something else.
 *
 * "Worth interrupting for" is `isUrgentAlert` — tampering, a crash, or any
 * critical severity — defined once alongside the alert metadata so the sound,
 * the highlighted rows and the dashboard card cannot disagree.
 */
export function useCriticalAlertSound() {
  const audioRef = useRef<HTMLAudioElement | null>(null);

  useEffect(() => {
    const el = new Audio('/alert-sound.mp3');
    el.preload = 'auto';
    audioRef.current = el;
    return () => { audioRef.current = null; };
  }, []);

  useSocketEvent('alert:new', (data: any) => {
    if (!data || !isUrgentAlert(data)) return;
    if (!isAlertSoundEnabled()) return;
    // Browsers reject playback until the user has interacted with the page;
    // a rejected promise here is expected and must not surface as an error.
    audioRef.current?.play().catch(() => { /* autoplay blocked */ });
  });
}
